import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight, FaCheckCircle, FaShieldAlt, FaGlobeAfrica, FaGlobeEurope,
  FaUserPlus, FaBoxOpen, FaInbox, FaHandshake, FaTruck,
  FaShoppingBag, FaSearchDollar, FaUserTie, FaTools,
  FaQuestionCircle, FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import { WHATSAPP_URL, EMAIL_URL } from '../utils/contact';

/**
 * HowItWorks page — segmented by user profile.
 *
 * The marketplace serves several distinct profiles whose journeys
 * are very different. Rather than mixing everything into one
 * confusing flow, the user picks their profile first and only sees
 * what matters to them.
 *
 * V1: Supplier journey is fully detailed (priority profile).
 * Other profiles show a clean "coming soon" card with a way to
 * express interest.
 */

const PROFILES = [
  {
    id: 'supplier',
    icon: FaBoxOpen,
    titleFr: 'Fournisseur',
    titleEn: 'Supplier',
    descFr: 'Je vends ou loue du matériel géotechnique',
    descEn: 'I sell or rent geotechnical equipment',
    color: '#635BFF',
    available: true,
  },
  {
    id: 'buyer',
    icon: FaShoppingBag,
    titleFr: 'Acheteur / Loueur',
    titleEn: 'Buyer / Renter',
    descFr: "Je cherche du matériel pour mon chantier",
    descEn: 'I need equipment for my project',
    color: '#00D4FF',
    available: false,
  },
  {
    id: 'talent',
    icon: FaUserTie,
    titleFr: 'Talent',
    titleEn: 'Talent',
    descFr: 'Je suis opérateur ou technicien',
    descEn: 'I am an operator or technician',
    color: '#10B981',
    available: false,
  },
  {
    id: 'recruiter',
    icon: FaTools,
    titleFr: 'Recruteur',
    titleEn: 'Recruiter',
    descFr: 'Je recrute pour mes chantiers',
    descEn: 'I am hiring for my projects',
    color: '#F59E0B',
    available: false,
  },
];

const SUPPLIER_STEPS_FR = [
  {
    icon: FaUserPlus,
    title: 'Inscrivez-vous gratuitement',
    description:
      "Créez votre compte fournisseur en 2 minutes. Renseignez votre entreprise, vos contacts et la zone géographique que vous couvrez (Europe, Afrique ou les deux).",
    detail: "Aucun engagement, aucun paiement à l'inscription.",
  },
  {
    icon: FaBoxOpen,
    title: 'Listez votre matériel',
    description:
      "Ajoutez vos foreuses, pompes, pressiomètres, équipements de fondations spéciales… Pour chaque équipement, indiquez s'il est à vendre, à louer (courte/longue durée) ou les deux.",
    detail: "Photos, fiche technique, prix ou « sur devis » — vous choisissez la visibilité.",
  },
  {
    icon: FaInbox,
    title: 'Recevez des demandes qualifiées',
    description:
      "Les acheteurs et loueurs filtrent par type de matériel et par localisation. Quand votre offre correspond, vous recevez la demande directement par email et dans votre tableau de bord.",
    detail: "Pas de prospection, pas de mailing froid : les demandes viennent à vous.",
  },
  {
    icon: FaHandshake,
    title: 'Échangez et envoyez votre devis',
    description:
      "Discutez avec l'acheteur via la messagerie intégrée, posez vos questions, ajustez votre proposition. Notre comparateur IA peut aider l'acheteur à analyser votre devis aux côtés des autres.",
    detail: "Multilingue : vous pouvez répondre en français, l'acheteur lit en anglais (ou inversement).",
  },
  {
    icon: FaTruck,
    title: 'Concluez la transaction',
    description:
      "Une fois le devis accepté, vous organisez la livraison et la facturation directement avec l'acheteur. Notre équipe est là pour vous accompagner sur les questions douanières Europe-Afrique.",
    detail: "Support dédié bilingue FR/EN à toutes les étapes.",
  },
];

const SUPPLIER_BENEFITS_FR = [
  {
    icon: FaShieldAlt,
    title: 'Visibilité ciblée B2B',
    desc: "Votre matériel est exposé aux pros qui cherchent vraiment, pas au grand public. Fini les leads non qualifiés.",
  },
  {
    icon: FaGlobeEurope,
    title: "Accès à l'Afrique",
    desc: "Beaucoup de matériel européen d'occasion trouve une seconde vie en Afrique. Nous facilitons cette mise en relation.",
  },
  {
    icon: FaSearchDollar,
    title: 'Modèle freemium',
    desc: "Inscription et listing de base gratuits. Passez en Premium quand vous voulez plus de visibilité ou de fonctionnalités avancées.",
  },
  {
    icon: FaCheckCircle,
    title: 'Pas de commission cachée',
    desc: "Vous gardez la relation directe avec vos clients. Pas de pourcentage prélevé sur vos transactions.",
  },
];

const FAQ_FR = [
  {
    q: "Combien ça coûte de s'inscrire ?",
    a: "L'inscription est 100% gratuite. Pour les 100 premiers fournisseurs, tous les plans payants sont offerts pendant 6 mois — vous accédez à toute la plateforme sans limite. Au-delà, le plan Découverte permet jusqu'à 3 annonces gratuites. Voir le détail sur la page Tarifs.",
  },
  {
    q: 'Vérifiez-vous les fournisseurs ?',
    a: "Oui. Chaque nouveau fournisseur passe par une étape de vérification (numéro SIRET ou équivalent local, justificatifs d'activité géotechnique). Cela garantit aux acheteurs qu'ils traitent avec des entreprises sérieuses, pas des particuliers ou des arnaqueurs.",
  },
  {
    q: 'Comment se passe le paiement entre fournisseur et acheteur ?',
    a: "Pour l'instant, GEOTS est un service de mise en relation. Le paiement se fait directement entre fournisseur et acheteur, selon les modalités convenues entre eux (acompte, virement, lettre de crédit pour l'export, etc.). Une option de paiement sécurisé via la plateforme est en cours d'évaluation.",
  },
  {
    q: "Et la livraison entre l'Europe et l'Afrique ?",
    a: "Le fournisseur et l'acheteur conviennent ensemble du mode de livraison : enlèvement par l'acheteur, transport organisé par le fournisseur, ou via un transporteur tiers. Nous pouvons vous mettre en relation avec des transitaires partenaires pour les expéditions intercontinentales.",
  },
  {
    q: 'Que se passe-t-il en cas de litige ?',
    a: "Notre équipe support intervient pour faciliter le dialogue entre les deux parties. Pour les transactions importantes, nous recommandons fortement un contrat écrit (modèle disponible sur demande) et une vérification physique du matériel avant paiement final.",
  },
  {
    q: 'Mes données et celles de mes clients sont-elles protégées ?',
    a: "Oui, nous sommes conformes RGPD. Vos données personnelles ne sont jamais revendues. Voir notre Politique de confidentialité pour le détail.",
  },
];

export default function HowItWorks() {
  const { i18n } = useTranslation();
  const [selectedProfile, setSelectedProfile] = useState('supplier');
  const [openFaq, setOpenFaq] = useState(null);
  const isFr = i18n.language === 'fr';

  const profile = PROFILES.find((p) => p.id === selectedProfile);

  const seoConfig = {
    title: {
      fr: 'Comment ça marche - Guide GEOTS Consulting',
      en: 'How it works - GEOTS Consulting Guide',
    },
    description: {
      fr: "Découvrez comment GEOTS Consulting connecte fournisseurs et acheteurs de matériel géotechnique entre l'Europe et l'Afrique. Inscription gratuite, vérification, mise en relation.",
      en: 'Discover how GEOTS Consulting connects geotechnical equipment suppliers and buyers across Europe and Africa. Free signup, verification, B2B matching.',
    },
    path: '/comment-ca-marche',
    image: '/og-image.jpg',
  };

  return (
    <div className="min-h-screen bg-white">
      <PageSeo {...seoConfig} />

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#1a365d] to-[#0A2540] pt-20 pb-16">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#635BFF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-20 w-80 h-80 bg-[#00D4FF]/15 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 mb-6">
            <FaGlobeEurope className="text-[#635BFF]" aria-hidden="true" />
            <span className="text-white/50">—</span>
            <FaGlobeAfrica className="text-[#00D4FF]" aria-hidden="true" />
            <span className="text-white/90 text-sm font-medium ml-2">
              {isFr ? 'Europe & Afrique' : 'Europe & Africa'}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {isFr ? (
              <>
                Comment ça marche{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#635BFF] to-[#00D4FF]">
                  pour vous ?
                </span>
              </>
            ) : (
              <>
                How does it work{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#635BFF] to-[#00D4FF]">
                  for you?
                </span>
              </>
            )}
          </h1>

          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
            {isFr
              ? "GEOTS Consulting réunit plusieurs profils. Sélectionnez le vôtre pour voir le parcours qui vous concerne."
              : 'GEOTS Consulting brings together several profiles. Pick yours to see the journey that matters to you.'}
          </p>

          {/* Profile selector */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {PROFILES.map((p) => {
              const Icon = p.icon;
              const isActive = selectedProfile === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedProfile(p.id)}
                  className={`relative p-5 rounded-2xl text-left transition-all border ${
                    isActive
                      ? 'bg-white border-white shadow-2xl scale-105'
                      : 'bg-white/5 backdrop-blur-sm border-white/20 hover:bg-white/10'
                  }`}
                  aria-pressed={isActive}
                  data-testid={`profile-${p.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      backgroundColor: isActive ? `${p.color}20` : 'rgba(255,255,255,0.1)',
                    }}
                  >
                    <Icon
                      className={isActive ? '' : 'text-white'}
                      style={{ color: isActive ? p.color : undefined, fontSize: '1.25rem' }}
                      aria-hidden="true"
                    />
                  </div>
                  <p
                    className={`font-bold text-sm mb-1 ${
                      isActive ? 'text-[#0A2540]' : 'text-white'
                    }`}
                  >
                    {isFr ? p.titleFr : p.titleEn}
                  </p>
                  <p
                    className={`text-xs ${
                      isActive ? 'text-gray-600' : 'text-white/60'
                    }`}
                  >
                    {isFr ? p.descFr : p.descEn}
                  </p>
                  {!p.available && (
                    <span className="absolute top-2 right-2 text-[10px] uppercase font-bold tracking-wider bg-white/20 text-white/80 px-2 py-0.5 rounded-full">
                      {isFr ? 'Bientôt' : 'Soon'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Selected profile journey ─── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex items-center gap-4 mb-12">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${profile.color}15` }}
            >
              <profile.icon
                style={{ color: profile.color, fontSize: '1.75rem' }}
                aria-hidden="true"
              />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wider text-gray-500 font-semibold mb-1">
                {isFr ? 'Parcours' : 'Journey'}
              </p>
              <h2 className="text-3xl font-bold text-[#0A2540]">
                {isFr ? profile.titleFr : profile.titleEn}
              </h2>
            </div>
          </div>

          {profile.available ? (
            <SupplierJourney isFr={isFr} />
          ) : (
            <ComingSoonProfile profile={profile} isFr={isFr} />
          )}
        </div>
      </section>

      {/* ─── FAQ (always visible) ─── */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#635BFF]/10 text-[#635BFF] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <FaQuestionCircle aria-hidden="true" />
              <span>FAQ</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-3">
              {isFr ? 'Questions fréquentes' : 'Frequently asked questions'}
            </h2>
            <p className="text-gray-600">
              {isFr
                ? 'Les réponses aux questions que se posent tous nos utilisateurs.'
                : 'Answers to the questions all our users ask.'}
            </p>
          </div>

          <div className="space-y-3">
            {FAQ_FR.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-semibold text-[#0A2540] pr-4">{item.q}</span>
                  {openFaq === i ? (
                    <FaChevronUp className="text-gray-400 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <FaChevronDown className="text-gray-400 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-gray-600 leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-600 mb-4">
              {isFr
                ? 'Une autre question ? Notre équipe répond sous 24h.'
                : 'Another question? Our team replies within 24h.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1fb558] transition-colors"
              >
                {isFr ? 'WhatsApp' : 'WhatsApp'}
              </a>
              <a
                href={EMAIL_URL}
                className="inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-[#0A2540] px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                {isFr ? 'Nous écrire' : 'Email us'}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-br from-[#635BFF] to-[#8B7DFF] rounded-3xl p-12 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {isFr ? 'Prêt à essayer ?' : 'Ready to try?'}
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
              {isFr
                ? 'Inscription gratuite. Aucune carte bancaire demandée. Vous testez, vous décidez.'
                : 'Free signup. No credit card needed. Try it, decide later.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#635BFF] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
              >
                {isFr ? 'Créer mon compte' : 'Create my account'} <FaArrowRight />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all"
              >
                {isFr ? 'Voir les tarifs' : 'See pricing'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Sub-component: detailed supplier journey ───
function SupplierJourney({ isFr }) {
  const steps = SUPPLIER_STEPS_FR;
  const benefits = SUPPLIER_BENEFITS_FR;

  return (
    <>
      {/* Steps */}
      <ol className="space-y-6 mb-16">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <li
              key={i}
              className="flex gap-5 p-6 bg-white border border-gray-200 rounded-2xl hover:border-[#635BFF]/30 hover:shadow-md transition-all"
            >
              {/* Number circle */}
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#635BFF] text-white flex items-center justify-center font-bold text-lg">
                  {i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className="w-0.5 flex-1 bg-gradient-to-b from-[#635BFF]/30 to-transparent mt-2"></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="text-[#635BFF] text-xl" aria-hidden="true" />
                  <h3 className="text-xl font-bold text-[#0A2540]">{step.title}</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-2">{step.description}</p>
                <p className="text-sm text-[#635BFF] font-medium italic">
                  → {step.detail}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Why join */}
      <div className="bg-gradient-to-br from-gray-50 to-[#635BFF]/5 rounded-3xl p-8 md:p-12">
        <h3 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-2 text-center">
          {isFr ? 'Pourquoi rejoindre GEOTS ?' : 'Why join GEOTS?'}
        </h3>
        <p className="text-center text-gray-600 mb-10">
          {isFr
            ? 'Les avantages concrets pour les fournisseurs de matériel géotechnique.'
            : 'Concrete benefits for geotechnical equipment suppliers.'}
        </p>

        <div className="grid md:grid-cols-2 gap-5">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center mb-4">
                  <Icon className="text-[#635BFF] text-xl" aria-hidden="true" />
                </div>
                <h4 className="font-bold text-[#0A2540] mb-2">{b.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Inline CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#635BFF] to-[#8B7DFF] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:shadow-[#635BFF]/25 transition-all"
          >
            {isFr ? 'Inscrire mon entreprise' : 'Register my company'} <FaArrowRight />
          </Link>
          <p className="text-xs text-gray-500 mt-3">
            {isFr
              ? 'Inscription gratuite · 2 minutes · Aucune carte demandée'
              : 'Free signup · 2 minutes · No card required'}
          </p>
        </div>
      </div>
    </>
  );
}

// ─── Sub-component: clean placeholder for non-supplier profiles ───
function ComingSoonProfile({ profile, isFr }) {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-3xl p-12 text-center">
      <div
        className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
        style={{ backgroundColor: `${profile.color}15` }}
      >
        <profile.icon style={{ color: profile.color, fontSize: '2rem' }} aria-hidden="true" />
      </div>

      <h3 className="text-2xl font-bold text-[#0A2540] mb-3">
        {isFr ? 'Parcours en préparation' : 'Journey coming soon'}
      </h3>

      <p className="text-gray-600 max-w-xl mx-auto mb-8 leading-relaxed">
        {isFr
          ? `Le parcours détaillé pour ${profile.titleFr.toLowerCase()}s arrive très prochainement. En attendant, le service est déjà fonctionnel et notre équipe peut vous accompagner pas à pas.`
          : `The detailed journey for ${profile.titleEn.toLowerCase()}s is coming soon. The service is already live and our team can guide you through it.`}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1fb558] transition-colors"
        >
          {isFr ? 'Discuter sur WhatsApp' : 'Chat on WhatsApp'}
        </a>
        <Link
          to="/register"
          className="inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-[#0A2540] px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
        >
          {isFr ? "M'inscrire quand même" : 'Sign up anyway'}
        </Link>
      </div>
    </div>
  );
}
